import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FormularioResposta, FormularioCampoResposta } from '@/types/formulario';
import { toast } from 'sonner';

export function useFormularioRespostas(formularioId?: string) {
  const queryClient = useQueryClient();

  // Buscar respostas de um formulário
  const { data: respostas, isLoading } = useQuery({
    queryKey: ['formulario_respostas', formularioId],
    queryFn: async () => {
      if (!formularioId) return [];
      
      const { data, error } = await supabase
        .from('formulario_respostas')
        .select(`
          *,
          formulario:formularios(id, titulo)
        `)
        .eq('formulario_id', formularioId)
        .order('criado_em', { ascending: false });

      if (error) throw error;

      // Buscar os dados dos respondentes
      const respondidoPorIds = data
        .map(r => r.respondido_por)
        .filter(id => id !== null);

      if (respondidoPorIds.length > 0) {
        const { data: usuarios } = await supabase
          .from('users')
          .select('id, name, email')
          .in('id', respondidoPorIds);

        // Fazer o merge dos dados
        const respostasComRespondentes = data.map(resposta => ({
          ...resposta,
          respondente: usuarios?.find(u => u.id === resposta.respondido_por) || null
        }));

        return respostasComRespondentes as any[];
      }

      return data as any[];
    },
    enabled: !!formularioId,
  });

  // Buscar uma resposta específica com seus campos preenchidos
  const getRespostaDetalhes = async (respostaId: string) => {
    const { data: resposta, error: respostaError } = await supabase
      .from('formulario_respostas')
      .select(`
        *,
        formulario:formularios(id, titulo, descricao)
      `)
      .eq('id', respostaId)
      .single();

    if (respostaError) throw respostaError;

    const { data: campos, error: camposError } = await supabase
      .from('formulario_campo_respostas')
      .select(`
        *,
        campo:formulario_campos(*)
      `)
      .eq('resposta_id', respostaId);

    if (camposError) throw camposError;

    return { resposta, campos };
  };

  // Criar nova resposta (rascunho)
  const { mutate: createResposta, isPending: isCreating } = useMutation({
    mutationFn: async (formularioId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Buscar client_id do usuário
      const { data: userData } = await supabase
        .from('users')
        .select('client_id')
        .eq('id', user.id)
        .single();

      const { data, error } = await supabase
        .from('formulario_respostas')
        .insert({
          formulario_id: formularioId,
          respondido_por: user.id,
          client_id: userData?.client_id,
          status: 'rascunho',
          data_inicio: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario_respostas'] });
    },
    onError: (error) => {
      toast.error('Erro ao criar resposta: ' + error.message);
    },
  });

  // Atualizar campos da resposta
  const { mutate: updateCampoResposta, isPending: isUpdating } = useMutation({
    mutationFn: async (params: {
      respostaId: string;
      campoId: string;
      valor?: string;
      valorJson?: any;
      arquivos?: string[];
    }) => {
      const { data, error } = await supabase
        .from('formulario_campo_respostas')
        .upsert({
          resposta_id: params.respostaId,
          campo_id: params.campoId,
          valor: params.valor,
          valor_json: params.valorJson,
          arquivos: params.arquivos,
          respondido_em: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Submeter resposta (mudar status de rascunho para concluído)
  const { mutate: submitResposta, isPending: isSubmitting } = useMutation({
    mutationFn: async (respostaId: string) => {
      const { data, error } = await supabase
        .from('formulario_respostas')
        .update({
          status: 'concluido',
          data_conclusao: new Date().toISOString(),
        })
        .eq('id', respostaId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Formulário enviado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['formulario_respostas'] });
    },
    onError: (error) => {
      toast.error('Erro ao enviar formulário: ' + error.message);
    },
  });

  // Deletar resposta
  const { mutate: deleteResposta, isPending: isDeleting } = useMutation({
    mutationFn: async (respostaId: string) => {
      const { error } = await supabase
        .from('formulario_respostas')
        .delete()
        .eq('id', respostaId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Resposta excluída');
      queryClient.invalidateQueries({ queryKey: ['formulario_respostas'] });
    },
    onError: (error) => {
      toast.error('Erro ao excluir resposta: ' + error.message);
    },
  });

  // Atualizar status da resposta (validado/rejeitado)
  const { mutate: updateRespostaStatus } = useMutation({
    mutationFn: async (params: { respostaId: string; status: 'validado' | 'rejeitado' }) => {
      const { data, error } = await supabase
        .from('formulario_respostas')
        .update({ status: params.status })
        .eq('id', params.respostaId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast.success(`Resposta ${variables.status === 'validado' ? 'validada' : 'rejeitada'}`);
      queryClient.invalidateQueries({ queryKey: ['formulario_respostas'] });
    },
    onError: (error) => {
      toast.error('Erro ao atualizar status: ' + error.message);
    },
  });

  return {
    respostas,
    isLoading,
    createResposta,
    isCreating,
    updateCampoResposta,
    isUpdating,
    submitResposta,
    isSubmitting,
    deleteResposta,
    isDeleting,
    getRespostaDetalhes,
    updateRespostaStatus,
  };
}
